import { useState, useRef, useEffect, type FormEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { useOrgStore } from '@/stores/orgStore'
import { useAuthStore } from '@/stores/authStore'
import { useChatStore } from '@/stores/chatStore'
import { usePageDataStore } from '@/stores/pageDataStore'
import { aiApi } from '@/api/clients/aiApi'
import { Markdown } from '@/components/common/Markdown'

const AI_OFF =
  'AI is currently unavailable (LLM off or degraded). The deterministic engine still governs all scores — use the pages and filters directly.'

// Linkify bare asset UUIDs the assistant cites → the asset detail page, so users can
// click through to verify evidence (UAT: evidence traceability). The lookbehind skips
// UUIDs already inside a path (/assets/<id>) or a markdown link.
const UUID_RE =
  /(?<![\w/[])[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(?![\w-])/g
// Only linkify plain prose — never inside fenced ``` blocks or `inline code`, where a
// UUID is usually a verbatim record the user is reading, not a nav target.
function linkifyAssetIds(md: string): string {
  const parts = md.split(/(```[\s\S]*?```|`[^`]*`)/g)
  return parts
    .map((seg, i) => (i % 2 === 1 ? seg : seg.replace(UUID_RE, (id) => `[${id}](/assets/${id})`)))
    .join('')
}

const SUGGESTIONS = [
  'Explain this page',
  'Which inventories carry the most $-at-risk?',
  'List critical assets',
  'What should we migrate first?',
]

// Global portfolio assistant (§5.6 / P4). Grounded in the org snapshot + the current
// page's published data (folded into the question); streams the answer via SSE.
export function ChatDrawer() {
  const { currentOrgId: orgId } = useOrgStore()
  const userId = useAuthStore((s) => s.userId) ?? ''
  const { isOpen, close, width, setWidth, messages, addMessage, updateLast, clear } = useChatStore()
  const pageData = usePageDataStore((s) => s.data)
  const location = useLocation()
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Resizable width — drag the left edge. Shared via the store (so the layout reserves
  // space); persisted across sessions; clamped to [340, min(920, 90vw)].
  const MIN_W = 340

  useEffect(() => {
    if (isOpen) bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isOpen])

  // Abort any in-flight stream on unmount (e.g. logout/navigation) so it stops
  // writing tokens into the shared store and setState never fires after unmount.
  useEffect(() => () => abortRef.current?.abort(), [])

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault()
    const maxW = Math.min(920, window.innerWidth * 0.9)
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(MIN_W, Math.min(maxW, window.innerWidth - ev.clientX))
      setWidth(next)
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.userSelect = ''
      localStorage.setItem('qtz-chat-width', String(useChatStore.getState().width))
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  if (!isOpen) return null

  const submit = (e: FormEvent) => {
    e.preventDefault()
    void sendText(q)
  }

  const sendText = async (text: string) => {
    const question = text.trim()
    if (!question || busy) return
    // First message of a fresh transcript → reset the server session window too, so a
    // "New conversation" (which clears the client) also drops prior server-side turns.
    const isNew = messages.length === 0
    setQ('')
    addMessage({ role: 'user', content: question })
    addMessage({ role: 'assistant', content: '' })
    setBusy(true)
    setStatus(null)

    // Fold the current page's context + rendered data into the prompt so the
    // assistant can explain what's on screen. Question first so it's never lost to
    // truncation; page JSON kept small to bound latency/tokens.
    const context = pageData
      ? `${question}\n\n[Current page: "${document.title || 'Risk Portal'}" (${location.pathname}). ` +
        `On-screen data (JSON): ${JSON.stringify(pageData).slice(0, 8000)}]`
      : question
    const req = { question: context, scope: 'auto', user_id: userId, reset: isNew }

    // Stream tokens as the model writes, and show a status label while tools run. The
    // token text is JSON-encoded server-side so markdown (tables/headings) stays intact.
    const controller = new AbortController()
    abortRef.current = controller
    let acc = ''
    try {
      await aiApi.chatStream(
        orgId,
        req,
        {
          onStatus: (_tool, label) => setStatus(label || 'Working…'),
          onToken: (t) => {
            acc += t
            setStatus(null)
            updateLast(acc)
          },
          onDone: () => {
            if (!acc.trim()) updateLast(AI_OFF, true)
          },
          onError: (msg) => updateLast(`Request failed: ${msg}`, true),
        },
        controller.signal
      )
    } catch {
      // Aborted (unmount/navigation): stop silently — do not touch state or retry.
      if (controller.signal.aborted) return
      // If tokens already streamed, keep the partial answer — re-sending would create
      // a duplicate server turn and double the LLM cost. Only fall back when nothing
      // streamed (the stream never opened).
      if (acc.trim()) {
        updateLast(acc, true)
      } else {
        try {
          const resp = await aiApi.chat(orgId, req)
          const answer = (resp.answer ?? '').trim()
          updateLast(answer || AI_OFF, !answer)
        } catch (e) {
          updateLast(`Request failed: ${(e as Error).message || 'unknown error'}`, true)
        }
      }
    } finally {
      abortRef.current = null
      if (!controller.signal.aborted) {
        setBusy(false)
        setStatus(null)
      }
    }
  }

  return (
    <div
      className={`fixed inset-y-0 right-0 z-50 w-full bg-surface-light dark:bg-surface-dark shadow-float dark:border-l dark:border-border-dark flex flex-col ${
        // Readiness (I4 §9o): an overlay at ≥ 1280px and a bottom sheet below,
        // so the page keeps its full width.
        location.pathname.startsWith('/readiness') ? 'max-[1279px]:!top-auto max-[1279px]:h-[70vh] max-[1279px]:!w-full max-[1279px]:rounded-t-2xl max-[1279px]:border-t max-[1279px]:border-border-light dark:max-[1279px]:border-border-dark' : ''
      }`}
      style={{ maxWidth: '100vw', width }}
    >
      {/* Left-edge drag handle to resize the drawer. */}
      <div
        onMouseDown={startResize}
        className="hidden sm:block absolute left-0 inset-y-0 w-1.5 -ml-0.5 cursor-col-resize group"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize assistant"
      >
        <div className="h-full w-px mx-auto bg-transparent group-hover:bg-primary/40 transition-colors" />
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light dark:border-border-dark">
        <div className="flex items-center gap-2">
          <span className="material-icons text-primary dark:text-secondary">forum</span>
          <span className="text-sm font-semibold text-text-light dark:text-text-dark">
            Portfolio Assistant
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clear}
            disabled={messages.length === 0}
            className="p-1 text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark disabled:opacity-40"
            aria-label="New conversation"
            title="New conversation"
          >
            <span className="material-icons">restart_alt</span>
          </button>
          <button
            onClick={close}
            className="p-1 text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
            aria-label="Close assistant"
          >
            <span className="material-icons">close</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Intro + suggestions — always shown so users keep the entry points. */}
        <div className="space-y-4">
          <div className="flex items-start gap-2.5">
              <span className="material-icons text-primary dark:text-secondary">auto_awesome</span>
              <div>
                <p className="text-sm font-semibold text-text-light dark:text-text-dark">
                  Hi — I'm your Portfolio Assistant.
                </p>
                <p className="mt-1 text-sm text-subtle-light dark:text-subtle-dark">
                  I answer in plain language, grounded in your live risk data. I can help you:
                </p>
              </div>
            </div>
            <ul className="space-y-1.5 text-sm text-text-light dark:text-text-dark">
              {[
                ['description', 'Explain the page you\'re on — what the numbers mean'],
                ['payments', 'Break down $-at-risk and the top-exposed systems'],
                ['shield', 'Find your most critical / quantum-vulnerable assets'],
                ['route', 'Advise what to migrate first and why'],
                ['rule', 'Summarise compliance posture across frameworks'],
              ].map(([icon, text]) => (
                <li key={text} className="flex items-start gap-2">
                  <span className="material-icons text-subtle-light dark:text-subtle-dark text-base mt-0.5">
                    {icon}
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <div>
              <p className="label-sm text-subtle-light dark:text-subtle-dark mb-1.5">Try asking</p>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => void sendText(s)}
                    className="px-2.5 py-1 text-xs rounded-full border border-border-light dark:border-border-dark text-subtle-light dark:text-subtle-dark hover:bg-primary/5 hover:text-text-light dark:hover:text-text-dark"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                m.role === 'user'
                  ? 'bg-primary text-white dark:bg-secondary dark:text-neutral whitespace-pre-wrap'
                  : m.error
                    ? 'bg-error/10 text-error whitespace-pre-wrap'
                    : 'bg-container-light dark:bg-container-dark text-text-light dark:text-text-dark'
              }`}
            >
              {m.role === 'assistant' && !m.error && m.content ? (
                <Markdown>{linkifyAssetIds(m.content)}</Markdown>
              ) : (
                m.content || (busy && i === messages.length - 1 ? '…' : '')
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-subtle-light dark:text-subtle-dark text-xs">
            <span className="material-icons animate-spin text-sm">sync</span> {status ?? 'Thinking…'}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={submit}
        className="p-3 border-t border-border-light dark:border-border-dark flex gap-2"
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask about your risk posture…"
          className="flex-1 px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <button
          type="submit"
          disabled={busy || !q.trim()}
          className="px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-40"
          aria-label="Send"
        >
          <span className="material-icons text-base">send</span>
        </button>
      </form>
    </div>
  )
}
